import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { FormGroup, FormArray, FormBuilder, Validators, FormControl } from '@angular/forms';
import { EmailRule } from '../../../models/feature-flag.model';

/**
 * Component for building and managing email-based targeting rules
 */
@Component({
  selector: 'cbre-email-rule',
  templateUrl: './email-rule.component.html',
  styleUrls: ['./email-rule.component.scss'],
  standalone: false,
})
export class EmailRuleComponent implements OnInit {
  /**
   * Parent form group
   */
  @Input() parentForm: FormGroup;

  /**
   * Event emitted when rule changes
   */
  @Output() ruleChanged = new EventEmitter<EmailRule>();

  /**
   * Email validation pattern
   */
  emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  /**
   * Error message for email validation
   */
  emailError: string = null;

  /**
   * Form control for new email input
   */
  newEmailControl = new FormControl('');

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    // Ensure the rule form group has a values array
    const ruleGroup = this.parentForm?.get('rule') as FormGroup;

    // Safely check if ruleGroup exists and has the contains method
    if (ruleGroup && !ruleGroup.contains('values')) {
      ruleGroup.addControl('values', this.fb.array([]));
    }

    // Initialize the new email control
    this.newEmailControl = this.fb.control('', [Validators.pattern(this.emailPattern)]);
  }

  /**
   * Get the values form array
   */
  get valuesArray(): FormArray {
    const formArray = this.parentForm?.get('rule.values') as FormArray;
    // Return a fallback empty FormArray if the actual form array doesn't exist
    return formArray || this.fb.array([]);
  }

  /**
   * Get the emails as string array
   */
  get emails(): string[] {
    return this.valuesArray?.value || [];
  }

  /**
   * Add a new email
   */
  addEmail(): void {
    const email = this.newEmailControl.value?.trim?.();
    if (!email) {
      return;
    }

    // Validate the email
    if (!this.emailPattern.test(email)) {
      this.emailError = 'Please enter a valid email address';
      return;
    }

    // Check for duplicates - safely using the emails getter
    if (this.emails && this.emails.includes(email)) {
      this.emailError = 'This email is already in the list';
      return;
    }

    // Clear any error
    this.emailError = null;

    // Add to form array (safely)
    if (this.valuesArray) {
      this.valuesArray.push(this.fb.control(email));
    }

    // Clear input
    this.newEmailControl.setValue('');

    // Update the rule type
    const ruleGroup = this.parentForm.get('rule') as FormGroup;
    if (ruleGroup) {
      ruleGroup.patchValue({ type: 'email' });
    }

    // Emit rule change event for parent components (especially for group rule)
    this.emitRuleChange();
  }

  /**
   * Remove an email
   */
  removeEmail(index: number): void {
    if (this.valuesArray) {
      this.valuesArray.removeAt(index);
      this.emitRuleChange();
    }
  }

  /**
   * Clear all emails
   */
  clearEmails(): void {
    if (this.valuesArray) {
      while (this.valuesArray.length > 0) {
        this.valuesArray.removeAt(0);
      }
      this.emitRuleChange();
    }
  }

  /**
   * Handle Enter key in the input field
   */
  onInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addEmail();
    }
  }

  /**
   * Emit rule change event
   */
  private emitRuleChange(): void {
    const rule: EmailRule = {
      type: 'email',
      values: this.emails,
    };

    this.ruleChanged.emit(rule);
  }
}
